import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function NewsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Sector - News API" description="Query sector data via the News API; request parameters, code examples and the response schema for the sector endpoint."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/news/sector" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Sector - News API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>

        {/* Get News by Category */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /news/category/:category
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves news articles filtered by specific category. This allows for targeted news consumption based on topics such as technology, politics, business, or entertainment.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">category</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        News category (e.g., &quot;business&quot;, &quot;technology&quot;, &quot;politics&quot;, &quot;entertainment&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/news/category/technology', '/news/category/healthcare']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Categories</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "world",         label: "World",         description: "Global news & events" },
                  { value: "nation",        label: "Nation",        description: "Domestic national news" },
                  { value: "business",      label: "Business",      description: "Corporate & financial news" },
                  { value: "technology",    label: "Technology",    description: "Tech industry & innovation" },
                  { value: "entertainment", label: "Entertainment", description: "Media & entertainment" },
                  { value: "science",       label: "Science",       description: "Research & discoveries" },
                  { value: "sports",        label: "Sports",        description: "Sports news & events" },
                  { value: "health",        label: "Health",        description: "Healthcare & medical news" },
                ].map(({ value, label, description }) => (
                    <div key={value} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                      <code className="mt-0.5 shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-mono font-medium text-blue-800">
                        {value}
                      </code>
                      <div>
                        <p className="text-xs text-gray-500">{description}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <p className="text-sm text-gray-600 mb-3">Same structure as Company News response.</p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">title <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Article headline</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">link <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Encoded URL to the full article</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">summary <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Article summary or excerpt</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">published <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Publication date (RFC 1123 format)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="News by Category" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">News by Category</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="news/category"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <p className="text-sm text-gray-500 italic mb-2">Response format identical to country news example.</p>
              </div>
            </div>
          </div>
        </div>



      </div>
    </div>
  );
}
